import { CommonModule } from '@angular/common';
import { Component, HostListener, OnDestroy, OnInit, ViewChild, ElementRef, Renderer2, AfterViewInit } from '@angular/core';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { PermissionForStudents } from '@edtech-ui/access-role-permissions/users-permissions-constants/student-roles-permissions.enum';
import { HasPermissionDirective } from '@edtech-ui/access-role-permissions/users-permissions-directives/has-permissions.directives';
import { Roles } from '@edtech-ui/constants/roles.enum';
import { UserDetailResponse } from '@edtech-ui/core/models/Classroom/user-detail';
import { CareerStatus, COURSE_TO_EDUCATION_LEVEL, EducationLevel, ExperienceYears } from '@edtech-ui/constants/user-profile.enum';
import { JOB_ROLES_AND_SKILLS } from '@edtech-ui/constants/job-roles-skills.constant';
import { AuthServiceService } from '@edtech-ui/core/services/authService/auth-service.service';
import { ToastrService } from 'ngx-toastr';
import { catchError, finalize, of, Subscription, tap } from 'rxjs';
import { RouterModule } from '@angular/router';
import { ResumeService } from '@edtech-ui/core/services/resume-service/resume.service';
import { ResumeListItem } from '@edtech-ui/core/models/resume-builder/get-user-resume';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { CollegeItem } from '@edtech-ui/core/services/institution.service';
import { CantFindCollegeModalComponent } from '@edtech-ui/shared/cant-find-college-modal/cant-find-college-modal.component';
import { CollegeAutocompleteComponent } from '@edtech-ui/shared/college-autocomplete/college-autocomplete.component';

// ── Custom Dropdown State ──────────────────────────────────────────────────────
interface DropdownState {
	open: boolean;
	selected: string;
	search: string;
}

// ── Professional Details ───────────────────────────────────────────────────────
interface ProfessionalDetails {
	companyTitle: string;
	currentRole: string;
	techStack: string;
	currentCtc: string;
	expectedCtc: string;
	city1: string;
	city2: string;
	city3: string;
}


@Component({
	selector: 'app-user-profile',
	standalone: true,
	imports: [ReactiveFormsModule, FormsModule, CommonModule, HasPermissionDirective, RouterModule, MatDialogModule, CollegeAutocompleteComponent],
	templateUrl: './user-profile.component.html',
	styleUrl: './user-profile.component.scss'
})
export class UserProfileComponent implements OnInit, OnDestroy, AfterViewInit {

	@ViewChild('phoneModal') phoneModalRef!: ElementRef;
	@ViewChild('emailModal') emailModalRef!: ElementRef;

	userRole: Roles = Roles.STUDENT;
	userProfileForm: FormGroup;
	userId: string | null = null;
	isLoading: boolean = false;
	subscription = new Subscription();
	PermissionForStudents = PermissionForStudents;
	CareerStatus = CareerStatus;

	// ── Career status ────────────────────────────────────
	selectedCareer: CareerStatus | null = CareerStatus.FRESHER;
	isCareerStatusLocked: boolean = false;

	// ── Skills ───────────────────────────────────────────
	skills: string[] = [];
	skillInput: string = '';
	suggestedSkills: string[] = [];

	// ── Resume ───────────────────────────────────────────
	resumeFile: File | null = null;
	existingResumeUrl: string | null = null;
	existingResumeName: string | null = null;
	savedResumes: ResumeListItem[] = [];

	initialState: string | null = null;

	// ── College selection ────────────────────────────────
	selectedInstitutionId: string | null = null;
	// College name as loaded from the profile. Older profiles store free text
	// without an institution_id; they may keep it until the field is edited.
	loadedCollegeName = '';
	loadedEducationLevel = '';

	// ── Contact update (OTP flow) ────────────────────────
	// Email
	newEmailInput: string = '';
	emailOtpInput: string = '';
	emailUpdateToken: string = '';
	showEmailUpdateModal: boolean = false;
	isEmailOtpLoading: boolean = false;
	emailUpdateError: string = '';
	// Phone
	newPhoneInput: string = '';
	phoneOtpInput: string = '';
	phoneUpdateToken: string = '';
	showPhoneUpdateModal: boolean = false;
	isPhoneOtpLoading: boolean = false;
	phoneUpdateError: string = '';

	// OTP Timers
	emailOtpTimer: number = 0;
	private emailOtpInterval: any;
	phoneOtpTimer: number = 0;
	private phoneOtpInterval: any;

	get currentSnapshot(): string {
		return JSON.stringify({
			form: this.userProfileForm.value,
			institutionId: this.selectedInstitutionId,
			career: this.selectedCareer,
			education: this.dropdowns['education'].selected,
			year: this.dropdowns['year'].selected,
			experience: this.dropdowns['experience'].selected,
			jobCategory: this.dropdowns['jobCategory'].selected,
			customJobCategory: this.customJobCategory,
			preferredJobTitles: this.preferredJobTitles,
			profDetails: this.professionalDetails,
			linkedin: this.linkedInUrl,
			skills: this.skills,
			resumeFile: this.resumeFile ? this.resumeFile.name : null,
			existingResumeUrl: this.existingResumeUrl,
			existingResumeName: this.existingResumeName
		});
	}

	get hasChanges(): boolean {
		if (!this.initialState) return true;
		return this.initialState !== this.currentSnapshot;
	}

	// ── Misc ─────────────────────────────────────────────
	progressPercent: number = 0;
	public highlightedFields: string[] = [];
	linkedInUrl: string = '';
	preferredJobTitles: string[] = [];
	jobTitleInput: string = '';
	customJobCategory: string = '';

	// ── Professional Details (Working Professional) ──────
	professionalDetails: ProfessionalDetails = {
		companyTitle: '',
		currentRole: '',
		techStack: '',
		currentCtc: '',
		expectedCtc: '',
		city1: '',
		city2: '',
		city3: '',
	};

	// ── Dropdown option lists ─────────────────────────────
	educationOptions: string[] = Object.values(EducationLevel);

	yearStringOptions: string[] = [];

	experienceOptions: string[] = Object.values(ExperienceYears);

	jobCategoryOptions: string[] = Object.keys(JOB_ROLES_AND_SKILLS);

	// ── Custom Dropdown State Map ─────────────────────────
	dropdowns: Record<string, DropdownState> = {
		education: { open: false, selected: '', search: '' },
		year: { open: false, selected: '', search: '' },
		experience: { open: false, selected: '', search: '' },
		jobCategory: { open: false, selected: '', search: '' },
	};

	constructor(
		private _authService: AuthServiceService,
		private _toastr: ToastrService,
		private router: Router,
		private route: ActivatedRoute,
		private renderer: Renderer2,
		private resumeService: ResumeService,
		private _dialog: MatDialog
	) {
		this.userProfileForm = new FormGroup({
			first_name: new FormControl('', [
				Validators.required,
				Validators.minLength(2),
				Validators.maxLength(50),
				Validators.pattern(/^[a-zA-Z\s\.\-']+$/)
			]),
			last_name: new FormControl('', [
				Validators.minLength(1),
				Validators.maxLength(50),
				Validators.pattern(/^[a-zA-Z\s\.\-']+$/)
			]),
			phone_email: new FormControl('', [Validators.email]),
			// Length rules are not applied here: a changed value must be picked from
			// the colleges list (see isCollegeSelectionRequired), and legacy free-text
			// values shorter than the search minimum must stay saveable.
			college_name: new FormControl('', [Validators.required]),
			phone_number: new FormControl({ value: '', disabled: true }),
			city: new FormControl('', [Validators.required]),
			pin_code: new FormControl(''),
		});

		// Generate year options (past 20 + next 4)
		const currentYear = new Date().getFullYear();
		for (let y = currentYear + 4; y >= currentYear - 20; y--) {
			this.yearStringOptions.push(String(y));
		}
	}

	ngOnInit(): void {
		this.userRole = Number(localStorage.getItem('role_id'));
		this.userId = localStorage.getItem('user_id');
		if (this.userId) {
			this.getUsers();
		} else {
			this.initialState = this.currentSnapshot;
		}
		this.userProfileForm.valueChanges.subscribe(() => this.recalculateProgress());

		this.subscription.add(
			this.route.queryParams.subscribe(params => {
				if (params['missingFields']) {
					this.highlightedFields = params['missingFields'].split(',');
				}
			})
		);

		this.subscription.add(
			this.resumeService.getStudentsResume(1, 10).subscribe(response => {
				if (response?.Success && response?.Data?.resumes) {
					this.savedResumes = response.Data.resumes;
				}
			})
		);

		// Clear highlight on input / value change
		['first_name', 'phone_email', 'phone_number', 'city', 'college_name'].forEach(field => {
			this.subscription.add(
				this.userProfileForm.get(field)?.valueChanges.subscribe(() => {
					const control = this.userProfileForm.get(field);
					if (control && control.dirty) {
						this.removeHighlight(field);
					}
				})
			);
		});
	}

	goToResumeBuilderPage(): void {
		this.router.navigate(['/student-dashboard/resume-builder'], {
			queryParams: {
				returnUrl: this.router.url
			}
		});
	}

	selectSavedResume(resumeId: string): void {
		this.router.navigate(['/student-dashboard/resume-preview', resumeId], {
			queryParams: {
				returnUrl: this.router.url
			}
		});
	}

	// ── College selection ────────────────────────────────
	/** True when the typed college hasn't been picked from the list (legacy unchanged values are allowed). */
	get isCollegeSelectionRequired(): boolean {
		if (this.selectedInstitutionId) return false;
		const current = (this.userProfileForm.get('college_name')?.value || '').trim();
		return current !== this.loadedCollegeName.trim();
	}

	get collegeNameControl(): FormControl {
		return this.userProfileForm.get('college_name') as FormControl;
	}

	selectInstitution(institution: CollegeItem): void {
		this.applyCollege(institution);
	}

	onCollegeSelectionCleared(): void {
		this.selectedInstitutionId = null;
	}

	onCantFindCollege(): void {
		const firstName = (this.userProfileForm.get('first_name')?.value || '').trim();
		const email = (this.userProfileForm.get('phone_email')?.value || '').trim();

		if (!firstName || !email) {
			this._toastr.error('Please add your first name and email to your profile before adding a new college.');
			return;
		}

		const dialogRef = this._dialog.open(CantFindCollegeModalComponent, {
			data: {
				firstName,
				lastName: (this.userProfileForm.get('last_name')?.value || '').trim(),
				email
			}
		});

		dialogRef.afterClosed().subscribe((college: CollegeItem | undefined) => {
			if (college) this.applyCollege(college);
		});
	}

	private applyCollege(college: CollegeItem): void {
		this.selectedInstitutionId = String(college.id);
		const patchData: { college_name: string; city?: string } = { college_name: college.name };
		// Only prefill city; it is the user's current location and may differ from the college's
		if (college.city && !this.userProfileForm.get('city')?.value) {
			patchData.city = college.city;
		}
		this.userProfileForm.patchValue(patchData, { emitEvent: false });
		this.userProfileForm.get('college_name')?.markAsDirty();
		this.removeHighlight('college_name');
		this.recalculateProgress();
	}

	removeHighlight(field: string): void {
		const index = this.highlightedFields.indexOf(field);
		if (index > -1) {
			this.highlightedFields.splice(index, 1);
		}
	}

	// ── Dropdown helpers ─────────────────────────────────
	toggleDropdown(key: string): void {
		const isOpen = this.dropdowns[key].open;
		// Close all first
		Object.keys(this.dropdowns).forEach(k => {
			this.dropdowns[k].open = false;
			this.dropdowns[k].search = '';
		});
		// Toggle selected
		this.dropdowns[key].open = !isOpen;
	}

	selectOption(key: string, value: string): void {
		this.dropdowns[key].selected = value;
		this.dropdowns[key].open = false;
		this.dropdowns[key].search = '';
		this.removeHighlight(key);

		if (key === 'jobCategory') {
			if (value !== 'Other') {
				const newSuggested = JOB_ROLES_AND_SKILLS[value] || [];
				this.suggestedSkills = newSuggested.filter(s => !this.skills.includes(s));
			} else {
				this.suggestedSkills = [];
			}
		}

		this.recalculateProgress();
	}

	filterOptions(options: string[], search: string): string[] {
		if (!search) return options;
		const q = search.toLowerCase();
		return options.filter(o => o.toLowerCase().includes(q));
	}

	/** Close dropdowns on outside click */
	closeAllDropdowns(event?: Event): void {
		Object.keys(this.dropdowns).forEach(k => {
			this.dropdowns[k].open = false;
			this.dropdowns[k].search = '';
		});
	}

	@HostListener('document:keydown.escape')
	onEscape(): void { this.closeAllDropdowns(); }

	// ── Career status ─────────────────────────────────────
	selectCareer(type: CareerStatus): void {
		if (this.isCareerStatusLocked) return;
		this.selectedCareer = type;
		this.recalculateProgress();
	}

	// ── Preferred Job Titles management ──────────────────
	addJobTitle(event?: Event | KeyboardEvent): void {
		if (event) event.preventDefault();
		const val = this.jobTitleInput.trim();
		if (!val) return;

		// Validation
		if (this.preferredJobTitles.length >= 5) {
			this._toastr.error('You can add up to 5 preferred job titles.');
			return;
		}
		if (val.length < 2 || val.length > 50) {
			this._toastr.error('Job title must be between 2 and 50 characters.');
			return;
		}
		const validPattern = /^[a-zA-Z0-9\s\-\.\#\+]+$/;
		if (!validPattern.test(val)) {
			this._toastr.error('Job title contains invalid characters.');
			return;
		}

		if (!this.preferredJobTitles.includes(val)) {
			this.preferredJobTitles.push(val);
			this.jobTitleInput = '';
			this.recalculateProgress();
		} else {
			this._toastr.warning('Job title already added.');
		}
	}

	removeJobTitle(index: number): void {
		this.preferredJobTitles.splice(index, 1);
		this.recalculateProgress();
	}

	// ── Skills management ────────────────────────────────
	addSkill(event?: Event | KeyboardEvent): void {
		if (event) event.preventDefault();
		const val = this.skillInput.trim();
		if (val && !this.skills.includes(val)) {
			this.skills.push(val);
			this.suggestedSkills = this.suggestedSkills.filter(
				s => s.toLowerCase() !== val.toLowerCase()
			);
			this.skillInput = '';
			this.removeHighlight('skills');
			this.recalculateProgress();
		}
	}

	addSuggestedSkill(skill: string): void {
		if (!this.skills.includes(skill)) {
			this.skills.push(skill);
			this.suggestedSkills = this.suggestedSkills.filter(s => s !== skill);
			this.removeHighlight('skills');
			this.recalculateProgress();
		}
	}

	removeSkill(index: number): void {
		this.skills.splice(index, 1);
		this.recalculateProgress();
	}

	// ── Resume handling ──────────────────────────────────
	onFileChange(event: Event): void {
		const input = event.target as HTMLInputElement;
		if (input.files && input.files.length > 0) {
			const file = input.files[0];
			if (file.size > 10 * 1024 * 1024) {
				this._toastr.error('File size must be under 10 MB.');
				return;
			}
			this.resumeFile = file;
			this.removeHighlight('resume');
			this.recalculateProgress();
		}
	}

	onFileDrop(event: DragEvent): void {
		event.preventDefault();
		const files = event.dataTransfer?.files;
		if (files && files.length > 0) {
			const file = files[0];
			const allowed = [
				'application/pdf', 'application/msword',
				'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
			];
			if (!allowed.includes(file.type)) {
				this._toastr.error('Only PDF, DOC, or DOCX files are allowed.');
				return;
			}
			if (file.size > 10 * 1024 * 1024) {
				this._toastr.error('File size must be under 10 MB.');
				return;
			}
			this.resumeFile = file;
			this.removeHighlight('resume');
			this.recalculateProgress();
		}
	}

	removeResume(event: Event): void {
		event.stopPropagation();
		this.resumeFile = null;
		this.recalculateProgress();
	}

	// ── Progress ─────────────────────────────────────────
	recalculateProgress(): void {
		let filled = 0;
		if (this.selectedCareer) filled++;
		const v = this.userProfileForm.value;
		if (v.first_name) filled++;
		if (v.phone_email) filled++;
		if (v.college_name) filled++;
		if (this.skills.length >= 1) filled++;
		if (this.resumeFile || this.existingResumeUrl) filled++;
		this.progressPercent = Math.round((filled / 6) * 100);
	}

	// ── Navigation ───────────────────────────────────────
	cancel(): void {
		const returnUrl = this.route.snapshot.queryParams['returnUrl'];
		returnUrl ? this.router.navigateByUrl(returnUrl) : this.router.navigate(['/student-dashboard']);
	}

	// ── Submit ───────────────────────────────────────────
	onSubmit(): void {
		this.userProfileForm.markAllAsTouched();
		if (!this.userProfileForm.valid) {
			this._toastr.error('Please fill all required fields correctly.');
			return;
		}

		if (this.isCollegeSelectionRequired) {
			this._toastr.error('Please select your college from the list, or use "Can\'t find your college?" to add it.');
			return;
		}

		if (!this.dropdowns['education'].selected) {
			this._toastr.error('Highest education is required.');
			return;
		}

		if (this.selectedCareer === CareerStatus.FRESHER) {
			if (!this.dropdowns['year'].selected) {
				this._toastr.error('Year of passing is required.');
				return;
			}
		}

		if (this.selectedCareer === CareerStatus.WORKING) {
			if (!this.professionalDetails.companyTitle?.trim()) {
				this._toastr.error('Current company title is required for working professionals.');
				return;
			}
			if (!this.professionalDetails.currentRole?.trim()) {
				this._toastr.error('Current role is required for working professionals.');
				return;
			}
			if (!this.dropdowns['experience'].selected) {
				this._toastr.error('Years of experience is required for working professionals.');
				return;
			}
			if (this.professionalDetails.currentCtc && isNaN(Number(this.professionalDetails.currentCtc))) {
				this._toastr.error('Current CTC must be a valid number.');
				return;
			}
			if (this.professionalDetails.expectedCtc && isNaN(Number(this.professionalDetails.expectedCtc))) {
				this._toastr.error('Expected CTC must be a valid number.');
				return;
			}
		}

		this.isLoading = true;
		const payload: any = { ...this.userProfileForm.value };

		const roleCategory = this.dropdowns['jobCategory'].selected;
		if (roleCategory) {
			payload.preferred_job_role_category = roleCategory === 'Other' ? this.customJobCategory : roleCategory;
		}
		if (this.preferredJobTitles.length > 0) {
			payload.preferred_job_title = this.preferredJobTitles.join(', ');
		} else {
			payload.preferred_job_title = '';
		}
		if (this.skills.length > 0) {
			payload.skills = this.skills;
		}

		if (this.selectedCareer) {
			payload.profile_type = this.selectedCareer;
			payload.education_level = this.dropdowns['education'].selected;
			payload.year_of_passing = this.dropdowns['year'].selected;

			if (this.selectedCareer === CareerStatus.FRESHER) {
				// year_of_passing is already set above
			} else if (this.selectedCareer === CareerStatus.WORKING) {
				payload.experience_years = (this.dropdowns['experience'].selected || '').trim();
				payload.company_title = (this.professionalDetails.companyTitle || '').trim().replace(/\s+/g, ' ');
				payload.current_role = (this.professionalDetails.currentRole || '').trim().replace(/\s+/g, ' ');
				payload.tech_stack = (this.professionalDetails.techStack || '').trim().replace(/\s+/g, ' ');
				payload.current_ctc = (this.professionalDetails.currentCtc || '').trim();
				payload.expected_ctc = (this.professionalDetails.expectedCtc || '').trim();
				payload.preferred_city1 = (this.professionalDetails.city1 || '').trim().replace(/\s+/g, ' ');
				payload.preferred_city2 = (this.professionalDetails.city2 || '').trim().replace(/\s+/g, ' ');
				payload.preferred_city3 = (this.professionalDetails.city3 || '').trim().replace(/\s+/g, ' ');
			}
		}

		if (this.linkedInUrl && this.linkedInUrl.trim() !== '') {
			const linkedInUrlLower = this.linkedInUrl.trim().toLowerCase();
			if (linkedInUrlLower.startsWith('http://')) {
				this._toastr.error('HTTP is not allowed. Please use HTTPS or enter the URL without the protocol.');
				this.isLoading = false;
				return;
			}

			const linkedInRegex = /^(https:\/\/)?(www\.)?linkedin\.com\/.*$/i;
			if (!linkedInRegex.test(this.linkedInUrl.trim())) {
				this._toastr.error('Please enter a valid LinkedIn URL');
				this.isLoading = false;
				return;
			}

			this.linkedInUrl = this.linkedInUrl.trim();
			if (!this.linkedInUrl.toLowerCase().startsWith('https://')) {
				this.linkedInUrl = 'https://' + this.linkedInUrl;
			}
			payload.linkedin_url = this.linkedInUrl;
		} else {
			payload.linkedin_url = '';
		}

		const formData = new FormData();
		const formValue = this.userProfileForm.value;
		formData.append('first_name', (formValue.first_name || '').trim());
		formData.append('last_name', (formValue.last_name || '').trim());
		formData.append('phone_email', formValue.phone_email || '');
		formData.append('college_name', formValue.college_name || '');
		if (this.selectedInstitutionId) {
			formData.append('institution_id', this.selectedInstitutionId);
		}
		formData.append('city', formValue.city || '');
		formData.append('pin_code', formValue.pin_code || '');

		formData.append('preferred_job_role_category', payload.preferred_job_role_category !== undefined ? (payload.preferred_job_role_category || '').trim() : '');
		formData.append('preferred_job_title', payload.preferred_job_title !== undefined ? (payload.preferred_job_title || '').trim() : '');
		formData.append('skills', payload.skills ? JSON.stringify(payload.skills) : '[]');
		formData.append('profile_type', payload.profile_type !== undefined ? (payload.profile_type || '').trim() : '');
		formData.append('education_level', payload.education_level !== undefined ? (payload.education_level || '').trim() : '');
		// Keep users.course (college dashboard) in sync with "Highest education", stored as
		// selected — but only when the user changed it, so an unrelated save never rewrites a
		// registration course (e.g. 'B.Tech') into its pre-filled profile label ('B.Tech. / B.E.').
		const educationLevel = (payload.education_level || '').trim();
		if (educationLevel && educationLevel !== this.loadedEducationLevel) {
			formData.append('course', educationLevel);
		}
		formData.append('year_of_passing', payload.year_of_passing !== undefined ? String(payload.year_of_passing).trim() : '');
		formData.append('experience_years', payload.experience_years !== undefined ? (payload.experience_years || '').trim() : '');
		formData.append('company_title', payload.company_title !== undefined ? (payload.company_title || '').trim().replace(/\s+/g, ' ') : '');
		formData.append('current_role', payload.current_role !== undefined ? (payload.current_role || '').trim().replace(/\s+/g, ' ') : '');
		formData.append('tech_stack', payload.tech_stack !== undefined ? (payload.tech_stack || '').trim().replace(/\s+/g, ' ') : '');
		formData.append('current_ctc', payload.current_ctc !== undefined ? String(payload.current_ctc || '').trim() : '');
		formData.append('expected_ctc', payload.expected_ctc !== undefined ? String(payload.expected_ctc || '').trim() : '');
		formData.append('preferred_city1', payload.preferred_city1 !== undefined ? (payload.preferred_city1 || '').trim().replace(/\s+/g, ' ') : '');
		formData.append('preferred_city2', payload.preferred_city2 !== undefined ? (payload.preferred_city2 || '').trim().replace(/\s+/g, ' ') : '');
		formData.append('preferred_city3', payload.preferred_city3 !== undefined ? (payload.preferred_city3 || '').trim().replace(/\s+/g, ' ') : '');
		formData.append('linkedin_url', payload.linkedin_url !== undefined ? (payload.linkedin_url || '').trim() : '');

		if (this.resumeFile) {
			formData.append('resume', this.resumeFile);
		}

		this.subscription.add(
			this._authService.patchUserEditProfile(formData).pipe(
				tap(response => {
					if (response.Success) {
						// Clear any lingering toast (e.g. the "complete your profile
						// before applying" warning that sent the user here) so it
						// doesn't stack with this fresh confirmation.
						this._toastr.clear();
						this._toastr.success(response.Message);
						if (payload.phone_email) {
							localStorage.setItem('email', payload.phone_email);
							this._authService.currentUserEmail.next(payload.phone_email);
						}
						const returnUrl = this.route.snapshot.queryParams['returnUrl'];
						if (returnUrl) {
							this.router.navigateByUrl(returnUrl);
						} else {
							// Reload profile to refresh state and get any new URLs (e.g. resume)
							this.resumeFile = null;
							this.getUsers();
						}
					} else {
						this._toastr.error(response.Message);
					}
				}),
				catchError(() => {
					this._toastr.error('Something went wrong. Please try again.');
					return of(null);
				}),
				finalize(() => { this.isLoading = false; })
			).subscribe()
		);
	}

	// ── Load user ────────────────────────────────────────
	getUsers(): void {
		if (!this.userId) return;
		this._authService.getUserDetails(this.userId).pipe(
			tap((response: UserDetailResponse) => {
				if (response.Success && response.Data) {
					if (response.Data?.phone_email) {
						localStorage.setItem('email', response.Data.phone_email);
						this._authService.currentUserEmail.next(response.Data.phone_email);
					}
					this.userProfileForm.patchValue({
						first_name: response.Data.first_name || '',
						last_name: response.Data.last_name || '',
						phone_email: response.Data.phone_email,
						phone_number: response.Data.phone_number,
						city: response.Data.city,
						pin_code: response.Data.pin_code,
					});
					// Patched silently so the college search doesn't fire and clear the saved selection
					this.loadedCollegeName = response.Data.college_name || '';
					this.selectedInstitutionId = response.Data.institution_id != null ? String(response.Data.institution_id) : null;
					this.userProfileForm.get('college_name')?.setValue(this.loadedCollegeName, { emitEvent: false });

					if (response.Data.preferred_city1)
						this.professionalDetails.city1 = response.Data.preferred_city1;

					if (response.Data.preferred_city2)
						this.professionalDetails.city2 = response.Data.preferred_city2;

					if (response.Data.preferred_city3)
						this.professionalDetails.city3 = response.Data.preferred_city3;

					// Populate profile type and details
					if (response.Data.profile_type) {
						this.selectedCareer = response.Data.profile_type as any;
						this.isCareerStatusLocked = true;
					}

					// Populate dropdowns
					// Fall back to the course when no education level is saved yet: a
					// registration course is mapped, a value saved from this form is used as is.
					const course = response.Data.course || '';
					const educationLevel = response.Data.education_level
						|| COURSE_TO_EDUCATION_LEVEL[course]
						|| (this.educationOptions.includes(course) ? course : undefined);
					if (educationLevel) this.dropdowns['education'].selected = educationLevel;
					this.loadedEducationLevel = educationLevel || '';
					if (response.Data.year_of_passing) this.dropdowns['year'].selected = response.Data.year_of_passing;
					if (response.Data.experience_years) this.dropdowns['experience'].selected = response.Data.experience_years;
					if (response.Data.preferred_job_role_category) {
						const isStandard = this.jobCategoryOptions.includes(response.Data.preferred_job_role_category);
						this.dropdowns['jobCategory'].selected = isStandard ? response.Data.preferred_job_role_category : 'Other';
						if (!isStandard) {
							this.customJobCategory = response.Data.preferred_job_role_category;
							this.suggestedSkills = [];
						} else {
							this.suggestedSkills = JOB_ROLES_AND_SKILLS[response.Data.preferred_job_role_category] || [];
						}
					}

					// Populate free-text fields
					if (response.Data.preferred_job_title) {
						this.preferredJobTitles = response.Data.preferred_job_title
							.split(',')
							.map((t: string) => t.trim())
							.filter((t: string) => t.length > 0);
					}
					if (response.Data.company_title) this.professionalDetails.companyTitle = response.Data.company_title;
					if (response.Data.current_role) this.professionalDetails.currentRole = response.Data.current_role;
					if (response.Data.tech_stack) this.professionalDetails.techStack = response.Data.tech_stack;
					if (response.Data.current_ctc) this.professionalDetails.currentCtc = response.Data.current_ctc;
					if (response.Data.expected_ctc) this.professionalDetails.expectedCtc = response.Data.expected_ctc;
					this.linkedInUrl = response.Data.linkedin_url || '';

					// Populate skills array
					if (response.Data.skills && Array.isArray(response.Data.skills)) {
						this.skills = response.Data.skills;
						// Remove from suggested skills
						this.suggestedSkills = this.suggestedSkills.filter(s => !this.skills.includes(s));
					}

					// Populate existing resume
					if (response.Data.resume_url) {
						this.existingResumeUrl = response.Data.resume_url;
						this.existingResumeName = (response.Data as any).resume_name || 'Existing_Resume.pdf';
					} else {
						this.existingResumeUrl = null;
						this.existingResumeName = null;
					}

					this.recalculateProgress();

					// Capture initial state to enable/disable Save button
					setTimeout(() => {
						this.initialState = this.currentSnapshot;
					}, 50);
				} else {
					this._toastr.error('Failed to load profile.');
				}
			}),
			catchError(() => {
				this._toastr.error('Something went wrong while loading profile.');
				return of(null);
			})
		).subscribe();
	}

	get isTeachers(): boolean { return this.userRole === Roles.TEACHER; }

	ngAfterViewInit(): void {
		if (this.phoneModalRef) {
			this.renderer.appendChild(document.body, this.phoneModalRef.nativeElement);
		}
		if (this.emailModalRef) {
			this.renderer.appendChild(document.body, this.emailModalRef.nativeElement);
		}
	}

	ngOnDestroy(): void {
		if (this.phoneModalRef && this.phoneModalRef.nativeElement.parentNode) {
			this.renderer.removeChild(document.body, this.phoneModalRef.nativeElement);
		}
		if (this.emailModalRef && this.emailModalRef.nativeElement.parentNode) {
			this.renderer.removeChild(document.body, this.emailModalRef.nativeElement);
		}
		if (this.phoneOtpInterval) clearInterval(this.phoneOtpInterval);
		if (this.emailOtpInterval) clearInterval(this.emailOtpInterval);
		this.subscription.unsubscribe();
	}

	// ── Email Update OTP flow ────────────────────────────
	requestEmailOtp(): void {
		const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
		if (!this.newEmailInput || !emailRegex.test(this.newEmailInput)) {
			this.emailUpdateError = 'Please enter a valid email address.';
			return;
		}
		this.emailUpdateError = '';
		this.isEmailOtpLoading = true;
		this._authService.requestContactUpdateOtp({ contact_type: 'email', new_contact: this.newEmailInput })
			.pipe(finalize(() => this.isEmailOtpLoading = false))
			.subscribe({
				next: (res) => {
					if (res?.Success) {
						this.emailUpdateToken = res.Data?.token;
						this._toastr.success(`OTP sent to ${this.newEmailInput}`);
						this.startEmailOtpTimer();
					} else {
						this.emailUpdateError = res?.Message || 'Failed to send OTP.';
					}
				},
				error: (err) => {
					this.emailUpdateError = err?.error?.Message || err?.error?.detail || 'Failed to send OTP.';
				}
			});
	}

	verifyEmailOtp(): void {
		if (!this.emailOtpInput || this.emailOtpInput.length < 4) {
			this.emailUpdateError = 'Please enter the OTP.';
			return;
		}
		this.emailUpdateError = '';
		this.isEmailOtpLoading = true;
		this._authService.verifyContactUpdateOtp({
			contact_type: 'email',
			new_contact: this.newEmailInput,
			otp: Number(this.emailOtpInput),
			token: this.emailUpdateToken
		}).pipe(finalize(() => this.isEmailOtpLoading = false))
			.subscribe({
				next: (res) => {
					if (res?.Success) {
						this._toastr.success('Email updated successfully!');
						localStorage.setItem('email', this.newEmailInput);
						this._authService.currentUserEmail.next(this.newEmailInput);
						this.closeEmailUpdateModal();
						this.getUsers();
					} else {
						this.emailUpdateError = res?.Message || 'Invalid OTP.';
					}
				},
				error: (err) => {
					this.emailUpdateError = err?.error?.Message || err?.error?.detail || 'Invalid OTP.';
				}
			});
	}

	openEmailUpdateModal(): void {
		this.showEmailUpdateModal = true;
		this.newEmailInput = '';
		this.emailOtpInput = '';
		this.emailUpdateToken = '';
		this.emailUpdateError = '';
	}

	closeEmailUpdateModal(): void {
		this.showEmailUpdateModal = false;
		this.newEmailInput = '';
		this.emailOtpInput = '';
		this.emailUpdateToken = '';
		this.emailUpdateError = '';
		if (this.emailOtpInterval) clearInterval(this.emailOtpInterval);
		this.emailOtpTimer = 0;
	}

	// ── Phone Update OTP flow ────────────────────────────
	requestPhoneOtp(): void {
		if (!this.newPhoneInput || this.newPhoneInput.length !== 10 || !/^\d{10}$/.test(this.newPhoneInput)) {
			this.phoneUpdateError = 'Please enter a valid 10-digit phone number.';
			return;
		}
		this.phoneUpdateError = '';
		this.isPhoneOtpLoading = true;
		this._authService.requestContactUpdateOtp({ contact_type: 'phone', new_contact: this.newPhoneInput })
			.pipe(finalize(() => this.isPhoneOtpLoading = false))
			.subscribe({
				next: (res) => {
					if (res?.Success) {
						this.phoneUpdateToken = res.Data?.token;
						this._toastr.success(`OTP sent to ${this.newPhoneInput}`);
						this.startPhoneOtpTimer();
					} else {
						this.phoneUpdateError = res?.Message || 'Failed to send OTP.';
					}
				},
				error: (err) => {
					this.phoneUpdateError = err?.error?.Message || err?.error?.detail || 'Failed to send OTP.';
				}
			});
	}

	verifyPhoneOtp(): void {
		if (!this.phoneOtpInput || this.phoneOtpInput.length < 4) {
			this.phoneUpdateError = 'Please enter the OTP.';
			return;
		}
		this.phoneUpdateError = '';
		this.isPhoneOtpLoading = true;
		this._authService.verifyContactUpdateOtp({
			contact_type: 'phone',
			new_contact: this.newPhoneInput,
			otp: Number(this.phoneOtpInput),
			token: this.phoneUpdateToken
		}).pipe(finalize(() => this.isPhoneOtpLoading = false))
			.subscribe({
				next: (res) => {
					if (res?.Success) {
						this._toastr.success('Phone number updated successfully!');
						this.closePhoneUpdateModal();
						this.getUsers();
					} else {
						this.phoneUpdateError = res?.Message || 'Invalid OTP.';
					}
				},
				error: (err) => {
					this.phoneUpdateError = err?.error?.Message || err?.error?.detail || 'Invalid OTP.';
				}
			});
	}

	openPhoneUpdateModal(): void {
		this.showPhoneUpdateModal = true;
		this.newPhoneInput = '';
		this.phoneOtpInput = '';
		this.phoneUpdateToken = '';
		this.phoneUpdateError = '';
	}

	closePhoneUpdateModal(): void {
		this.showPhoneUpdateModal = false;
		this.newPhoneInput = '';
		this.phoneOtpInput = '';
		this.phoneUpdateToken = '';
		this.phoneUpdateError = '';
	}

	allowOnlyNumbers(event: any): boolean {
		const charCode = (event.which) ? event.which : event.keyCode;
		if (charCode > 31 && (charCode < 48 || charCode > 57)) {
			return false;
		}
		return true;
	}

	startEmailOtpTimer(): void {
		this.emailOtpTimer = 120;
		if (this.emailOtpInterval) clearInterval(this.emailOtpInterval);
		this.emailOtpInterval = setInterval(() => {
			if (this.emailOtpTimer > 0) {
				this.emailOtpTimer--;
			} else {
				clearInterval(this.emailOtpInterval);
			}
		}, 1000);
	}

	resendEmailOtp(): void {
		if (this.emailOtpTimer > 0) return;
		this.requestEmailOtp();
	}

	startPhoneOtpTimer(): void {
		this.phoneOtpTimer = 120;
		if (this.phoneOtpInterval) clearInterval(this.phoneOtpInterval);
		this.phoneOtpInterval = setInterval(() => {
			if (this.phoneOtpTimer > 0) {
				this.phoneOtpTimer--;
			} else {
				clearInterval(this.phoneOtpInterval);
			}
		}, 1000);
	}

	resendPhoneOtp(): void {
		if (this.phoneOtpTimer > 0) return;
		this.requestPhoneOtp();
	}
}